import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import HomeIcon from "@mui/icons-material/Home";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { downloadOrderSummary } from "./orderDownloadUtils";

const NursingHomeOrderConfirmationMobile = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [orderReference, setOrderReference] = useState("");
  const [orderName, setOrderName] = useState("");

  const orderState = location.state;

  if (!orderState || !orderState.formData) {
    return <Navigate to="/nursinghome" replace />;
  }

  const { formData, contactName, phoneNum, products, deliveryDays, sites } = orderState;

  const getProductDetails = (partname) => products?.find((p) => p.PARTNAME === partname);

  const getDeliveryDateLabel = () => {
    if (!formData.dueDate) return "לא נבחר";
    const dateStr = formData.dueDate.split("T")[0];
    const match = deliveryDays?.find((d) => d.date === dateStr);
    if (match) return match.formatted;
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      setError(null);
      const response = await axios.post(
        "/nursinghome/submit-order",
        {
          reference: formData.reference,
          dueDate: formData.dueDate,
          items: formData.items,
          notes: formData.notes,
          driverText: formData.driverText || "",
          files: formData.selectedFiles,
          dcode: formData.dcode || undefined,
        },
        { withCredentials: true },
      );
      if (response.data.success) {
        setOrderReference(response.data.orderReference);
        setOrderName(response.data.orderName || "");
        setSuccessDialogOpen(true);
      }
    } catch (err) {
      if (err.response?.status === 403) setError("אין הרשאה לשלוח הזמנה. אנא התחבר מחדש.");
      else if (err.response?.status === 400) setError("הזמנה לא הצליחה להקלט למערכת, נא לפנות לשירות לקוחות");
      else if (err.response?.status === 404) setError("לא ניתן למצוא את נתוני הלקוח. אנא פנה לתמיכה.");
      else if (err.response?.status === 409) setError("הזמנה עם מספר זה כבר קיימת. אנא השתמש במספר אחר.");
      else if (err.response?.status >= 500) setError("שגיאת שרת. אנא נסה שוב מאוחר יותר.");
      else if (err.response?.data?.msg) setError(`שגיאה: ${err.response.data.msg}`);
      else setError(`שגיאה בשליחת ההזמנה: ${err.message || "נסה שוב מאוחר יותר"}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate("/nursinghome", { state: { formData } });
  };

  const handleNewOrder = () => {
    setSuccessDialogOpen(false);
    navigate("/nursinghome");
  };

  const handleBackToHome = () => {
    navigate("/portal");
  };

  return (
    <Box sx={{ pb: 12, px: 2, pt: 1 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5, mb: 1 }}>
        <CheckCircleOutlineIcon sx={{ color: "#1976d2", fontSize: 28 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem", lineHeight: 1.2 }}>
            סיכום הזמנה
          </Typography>
          <Typography variant="caption" color="text.secondary">
            אנא בדוק/י את הפרטים לפני השליחה
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Order Details */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: "12px !important" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            פרטי הזמנה
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {formData.reference && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">מס' הזמנת רכש</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formData.reference}</Typography>
              </Box>
            )}
            {formData.dcode && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">אתר אספקה</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {sites?.find((s) => s.CODE === formData.dcode)?.CODEDES || formData.dcode}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">צפי ליום הפצה</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{getDeliveryDateLabel()}</Typography>
            </Box>
            {formData.notes && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>הערות</Typography>
                  <Typography variant="body2">{formData.notes}</Typography>
                </Box>
              </>
            )}
            {formData.driverText && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>הוראות לנהג</Typography>
                  <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{formData.driverText}</Typography>
                </Box>
              </>
            )}
            {formData.selectedFiles.length > 0 && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>קבצים מצורפים</Typography>
                  {formData.selectedFiles.map((file, index) => (
                    <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <AttachFileIcon fontSize="small" color="action" />
                      <Typography variant="caption">{file.name} ({(file.size / 1024).toFixed(1)} KB)</Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Items as Cards */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: "12px !important" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            פריטים ({formData.items.length})
          </Typography>
          {formData.items.map((item, index) => {
            const product = getProductDetails(item.partname);
            return (
              <Box key={index}>
                {index > 0 && <Divider sx={{ my: 1.5 }} />}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1, pr: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.partname}
                    </Typography>
                    {product?.PARTDES && (
                      <Typography variant="caption" color="text.secondary">
                        {product.PARTDES}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: "left", minWidth: 60 }}>
                    <Typography variant="caption" color="text.secondary">כמות</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.quantity}</Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </CardContent>
      </Card>

      {/* Sticky Action Bar */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "white",
          borderTop: "1px solid",
          borderColor: "divider",
          px: 2,
          py: 1.5,
          display: "flex",
          gap: 1.5,
          zIndex: 1000,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          disabled={submitting}
          fullWidth
        >
          חזור
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? null : <SendIcon />}
          onClick={handleConfirm}
          disabled={submitting}
          fullWidth
        >
          {submitting ? <CircularProgress size={22} /> : "אישור ושליחה"}
        </Button>
      </Box>

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ direction: "ltr", pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "green" }}>
            ההזמנה נקלטה בהצלחה
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ direction: "ltr", pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ direction: "ltr" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                מספר הזמנת רכש:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{orderReference}</Typography>
            </Box>

            {orderName && (
              <Box sx={{ direction: "ltr" }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  מספר הזמנה במערכת:
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#e3f2fd",
                    border: "2px solid #1976d2",
                    padding: 2,
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h4" sx={{ color: "primary.main", fontWeight: 700 }}>
                    {orderName}
                  </Typography>
                </Box>
              </Box>
            )}

            <Alert severity="warning" sx={{ direction: "ltr" }}>
              <Typography variant="body2">
                לעדכונים/שינויים בהזמנה יש לפנות לצוות שירות הלקוחות.
              </Typography>
            </Alert>

            {orderName && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<DownloadIcon />}
                onClick={() =>
                  downloadOrderSummary({
                    orderName,
                    orderReference,
                    formData,
                    products,
                    deliveryDays,
                    sites,
                  })
                }
                sx={{ py: 1.5 }}
              >
                הורד מספר הזמנה
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ direction: "rtl", justifyContent: "flex-start", px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleNewOrder} variant="contained" startIcon={<AddShoppingCartIcon sx={{ ml: 1 }} />} fullWidth>
            הזמנה חדשה
          </Button>
          <Button onClick={handleBackToHome} variant="outlined" startIcon={<HomeIcon sx={{ ml: 1 }} />} fullWidth>
            דף הבית
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NursingHomeOrderConfirmationMobile;
